import { useContext, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SeleccionContext from "../../../../../context/Seleccion/SeleccionContext";
import { formatDateToDDMMAAAA } from "../../../../../functions/kit-mdtw/dates";
import { ICandidatoSolRecibida } from "../../../../../interfaces/ISeleccion";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CloseIcon from "@mui/icons-material/Close";
import "./ModalResponderSolicitud.css";

interface IModalAceptarRegistro {
  rut: any;
  solicitudes: any;
  setSolicitudes: Function;
  onClose: () => void;
  onConfirm: () => void;
}

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.22 } },
  exit: { opacity: 0, transition: { duration: 0.18, delay: 0.05 } },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.92, y: 40 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { type: "spring" as const, stiffness: 260, damping: 22 } },
  exit: { opacity: 0, scale: 0.95, y: 20, transition: { duration: 0.17 } },
};

export default (props: IModalAceptarRegistro) => {
  const { rut, setSolicitudes, onClose, onConfirm } = props;
  const { listaCandidatoSolRecibida } = useContext(SeleccionContext);
  const [currentData, setCurrentData] = useState<ICandidatoSolRecibida[]>([]);
  const [comentarios, setComentarios] = useState<{ [key: string]: string }>({});
  const [comentarioMasivoActive, setComentarioMasivoActive] = useState(false);
  const [comentarioMasivo, setComentarioMasivo] = useState("");

  const isMasivo = currentData.length > 1;
  const mouseDownOnBackdrop = useRef(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    if (rut && listaCandidatoSolRecibida) {
      const current = listaCandidatoSolRecibida.filter(
        (item: ICandidatoSolRecibida) =>
          rut.includes(item.personaDNI + "-" + item.solicitudTraspasoId) &&
          item.estado !== "APROBADA" &&
          item.estado !== "RECHAZADA"
      );
      setCurrentData(current);
      const initial: { [key: string]: string } = {};
      current.forEach(({ solicitudTraspasoId }: ICandidatoSolRecibida) => {
        initial[solicitudTraspasoId] = "";
      });
      setComentarios(initial);
    }
  }, [rut]);

  useEffect(() => {
    const sol = currentData.map(({ solicitudTraspasoId }: ICandidatoSolRecibida) => ({
      solicitudTraspasoId,
      aceptar: true,
      comentario: comentarioMasivoActive ? comentarioMasivo : (comentarios[solicitudTraspasoId] ?? ""),
    }));
    setSolicitudes(sol);
  }, [currentData, comentarios, comentarioMasivoActive, comentarioMasivo]);

  return (
    <motion.div
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.50)",
        backdropFilter: "blur(3px)",
        zIndex: 1300,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "0 20px",
        overflowY: "auto",
      }}
      onMouseDown={(e) => { mouseDownOnBackdrop.current = e.target === e.currentTarget; }}
      onMouseUp={(e) => { if (mouseDownOnBackdrop.current && e.target === e.currentTarget) onClose(); mouseDownOnBackdrop.current = false; }}
    >
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        style={{
          background: "#FFFFFF",
          borderRadius: "18px",
          width: "100%",
          maxWidth: "640px",
          margin: "44px auto",
          boxShadow: "0 16px 48px rgba(0,0,0,0.12), 0 4px 14px rgba(0,0,0,0.06)",
          overflow: "hidden",
          fontFamily: "Inter, sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #16a34a 0%, #059669 60%, #0d9488 100%)",
          padding: "18px 22px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          position: "relative",
        }}>
          <div style={{
            width: "40px", height: "40px",
            background: "rgba(255,255,255,0.2)",
            borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "2px solid rgba(255,255,255,0.3)",
            flexShrink: 0,
          }}>
            <TaskAltIcon sx={{ fontSize: 22, color: "#ffffff" }} />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: "10px", color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.8px" }}>
              Confirmación
            </p>
            <h2 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "#ffffff" }}>
              Aprobar solicitud{isMasivo ? "es" : ""}
            </h2>
          </div>
          <motion.button
            onClick={onClose}
            style={{
              position: "absolute", top: "12px", right: "12px",
              background: "rgba(0,0,0,0.1)", border: "none", borderRadius: "7px",
              width: "28px", height: "28px",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#ffffff",
            }}
            whileHover={{ scale: 1.1, backgroundColor: "rgba(255,255,255,0.22)" }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400, damping: 20 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </motion.button>
        </div>

        {/* Subtitle */}
        <div style={{ padding: "14px 22px 0", fontSize: "13px", color: "#64748b" }}>
          {isMasivo
            ? `Estás aprobando ${currentData.length} solicitudes. Puedes agregar un comentario por cada una o usar el comentario masivo.`
            : "Confirma que deseas aprobar esta solicitud. Puedes agregar un comentario de respuesta."}
        </div>

        {/* Body */}
        <div style={{ padding: "14px 22px", display: "flex", flexDirection: "column", gap: "14px" }}>
          {isMasivo && (
            <div className="responder-masivo-controls">
              <label className="responder-masivo-checkbox-label">
                <input
                  type="checkbox"
                  checked={comentarioMasivoActive}
                  onChange={(e) => setComentarioMasivoActive(e.target.checked)}
                  className="responder-masivo-checkbox"
                />
                <span className="responder-masivo-switch" />
                Comentario masivo
              </label>
              <div className={`responder-masivo-field${comentarioMasivoActive ? " visible" : ""}`}>
                <div>
                  <input
                    type="text"
                    className="responder-comentario-input"
                    placeholder="Comentario para todos los registros..."
                    value={comentarioMasivo}
                    onChange={(e) => setComentarioMasivo(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="responder-cards-container">
            {currentData.map((item: ICandidatoSolRecibida) => {
              const { solicitudTraspasoId, personaDNI, primerNombre, primerApellido, procesoOrigen, procesoDestino, fechaSolicitud, propietario, comentario } = item;
              return (
                <div key={solicitudTraspasoId} className="responder-card">
                  <div className="responder-card-body">
                    <div className="responder-card-row">
                      <span className="responder-card-label">RUT</span>
                      <span className="responder-card-value">{personaDNI}</span>
                    </div>
                    <div className="responder-card-row">
                      <span className="responder-card-label">Nombre</span>
                      <span className="responder-card-value">{primerNombre} {primerApellido}</span>
                    </div>
                    <div className="responder-card-row">
                      <span className="responder-card-label">ID Origen</span>
                      <span className="responder-card-value">{procesoOrigen}</span>
                    </div>
                    <div className="responder-card-row">
                      <span className="responder-card-label">ID Destino</span>
                      <span className="responder-card-value responder-badge-destino">{procesoDestino}</span>
                    </div>
                    <div className="responder-card-row">
                      <span className="responder-card-label">Solicitante</span>
                      <span className="responder-card-value">{propietario}</span>
                    </div>
                    <div className="responder-card-row">
                      <span className="responder-card-label">Fecha solicitud</span>
                      <span className="responder-card-value">{formatDateToDDMMAAAA({ date: fechaSolicitud, format: "/" })}</span>
                    </div>
                  </div>
                  <div className="responder-card-footer">
                    <span className="responder-card-footer-label">Comentario de solicitud</span>
                    <input
                      type="text"
                      className="responder-comentario-input"
                      value={comentario ?? ""}
                      disabled
                      readOnly
                    />
                    <span className="responder-card-footer-label" style={{ marginTop: "12px" }}>Comentario de respuesta</span>
                    <input
                      type="text"
                      className="responder-comentario-input"
                      placeholder={comentarioMasivoActive ? "Usando comentario masivo..." : "Escribe tu respuesta (opcional)..."}
                      value={comentarioMasivoActive ? comentarioMasivo : (comentarios[solicitudTraspasoId] ?? "")}
                      disabled={comentarioMasivoActive}
                      onChange={(e) => {
                        if (!comentarioMasivoActive) {
                          setComentarios((prev) => ({ ...prev, [solicitudTraspasoId]: e.target.value }));
                        }
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 22px 20px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          borderTop: "1px solid #e2e8f0",
        }}>
          <motion.button
            onClick={onClose}
            style={{
              background: "#f1f5f9", border: "1px solid #e2e8f0",
              borderRadius: "9px", padding: "8px 20px",
              fontSize: "14px", fontWeight: 600, color: "#475569",
              cursor: "pointer", fontFamily: "Inter, sans-serif",
            }}
            whileHover={{ scale: 1.03, y: -2, backgroundColor: "#e2e8f0", color: "#1e293b", boxShadow: "0 4px 14px rgba(0,0,0,0.10)" }}
            whileTap={{ scale: 0.96, y: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 22 }}
          >
            Cerrar
          </motion.button>
          <motion.button
            onClick={onConfirm}
            style={{
              background: "linear-gradient(135deg, #16a34a, #059669)",
              border: "none", borderRadius: "9px", padding: "8px 22px",
              fontSize: "14px", fontWeight: 600, color: "#ffffff",
              cursor: "pointer", fontFamily: "Inter, sans-serif",
              boxShadow: "0 2px 8px rgba(22,163,74,0.35)",
            }}
            whileHover={{ scale: 1.05, y: -3, boxShadow: "0 8px 22px rgba(22,163,74,0.50)" }}
            whileTap={{ scale: 0.96, y: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 22 }}
          >
            Confirmar
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};
